/**
 * PostHog product analytics + session replay for the React app.
 *
 * Runs alongside the existing tools (Microsoft Clarity for replay, Crisp for chat,
 * Umami for a few conversions) and mirrors their per-user identify pattern. PostHog
 * is being trialed as a unified observability layer: the SAME project token as the
 * backend is used, and we identify with the DB user id, so a user's frontend
 * behavior links to their backend logs/errors in one timeline.
 *
 * Safe no-op when REACT_APP_POSTHOG_KEY is absent (e.g. local dev without a key),
 * so importing/calling these helpers never throws or blocks the app.
 */

// eslint-disable-next-line import/no-extraneous-dependencies
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let initialized = false;

/**
 * Initialize PostHog. Call once, as early as possible (before React renders).
 * Idempotent and a no-op when no key is configured.
 */
export function initPostHog () {
  if (initialized || typeof window === 'undefined' || !POSTHOG_KEY) {
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    // Opt into PostHog's current recommended defaults.
    defaults: '2026-01-30',
    // Only create person profiles for identified (logged-in) users — anonymous
    // events are still captured and get linked on identify.
    person_profiles: 'identified_only',

    // The PostHog cookie is shared across *.voolist.com, but localStorage is per-origin.
    // When the two disagree, make the shared cookie win: otherwise this origin's stale
    // localStorage overrides the session the marketing site just wrote, and a visitor
    // clicking through from www starts a phantom new session mid-journey.
    __preview_cookie_wins_on_conflict: true,

    // --- Automatic capture (the backbone) ---
    autocapture: true, // every click / input / form, with element + text + selector
    capture_pageview: true, // SPA route changes
    capture_pageleave: true,
    rageclick: true, // frustration: >3 clicks in 1s — "why isn't this working?!"
    capture_dead_clicks: true, // clicks that change nothing — "I clicked it and nothing happened"

    // --- Session replay (runs alongside Clarity during the trial) ---
    // Privacy-first: mask every input value; passwords are always masked. Tag any
    // remaining sensitive nodes with the `ph-no-capture` CSS class to block them.
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: { password: true },
    },

    // Frontend exception capture ($exception → Error Tracking) is enabled in the
    // PostHog project settings (Error tracking → Exception autocapture). The
    // ErrorBoundary also reports React render errors via capturePostHogException().
  });

  // Tag every event with the environment so dev/prod are filterable (matches the
  // backend's deployment.environment). CRA sets NODE_ENV to 'production' in builds.
  try {
    posthog.register({ environment: process.env.NODE_ENV });
  } catch (_) { /* noop */ }

  initialized = true;
}

export { posthog };

export function isPostHogEnabled () {
  return initialized;
}

/**
 * Identify the logged-in user. Uses the DB user id (same id the backend uses) so
 * frontend events link to backend logs/errors. Falls back to Firebase uid.
 * @param {Object|null} user - synced user ({ ...firebaseUser, ...dbProfile })
 */
export function identifyPostHogUser (user) {
  if (!initialized || !user) {
    return;
  }
  let distinctId = null;
  if (user.id != null) {
    distinctId = String(user.id);
  } else if (user.uid) {
    distinctId = String(user.uid);
  }
  if (!distinctId) {
    return;
  }
  try {
    posthog.identify(distinctId, {
      email: user.email,
      name: user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      role: user.role,
      plan: user.subscriptionPlan || user.plan || undefined,
      firebase_uid: user.uid,
    });
  } catch (_) {
    // never throw from telemetry
  }
}

/**
 * Clear identity on logout so the next user isn't merged into this one.
 */
export function resetPostHog () {
  if (!initialized) {
    return;
  }
  try {
    posthog.reset();
  } catch (_) { /* noop */ }
}

/**
 * Capture a named product event. No-op when PostHog is disabled.
 * @param {string} event - use a constant from ANALYTICS_EVENTS
 * @param {Object} [properties]
 */
export function trackEvent (event, properties) {
  if (!initialized) {
    return;
  }
  try {
    posthog.capture(event, properties);
  } catch (_) { /* noop */ }
}

/**
 * Report an exception to PostHog Error Tracking. No-op when disabled.
 */
export function capturePostHogException (error, properties) {
  if (!initialized) {
    return;
  }
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, properties);
  } catch (_) { /* noop */ }
}

/**
 * Record that a user tried to use a CTA that was disabled/gated. Disabled buttons
 * don't emit DOM clicks, so this is fired explicitly from the gated component.
 * @param {string} cta - logical name, e.g. 'listing.publish'
 * @param {string} reason - why it was blocked, e.g. 'no_platform_connected'
 */
export function trackBlockedCta (cta, reason, properties) {
  trackEvent(ANALYTICS_EVENTS.CTA_BLOCKED, { cta, reason, ...properties });
}

/**
 * Central event taxonomy. Mirrors the backend convention `<entity>.<action>.<phase>`
 * so frontend and backend events read consistently in one PostHog project.
 * Import these constants instead of hardcoding strings.
 */
export const ANALYTICS_EVENTS = Object.freeze({
  // --- Help center ---
  // A seller opened a help article. What sellers open is what the help center's popular list should follow.
  HELP_ARTICLE_VIEWED: 'help.article.viewed',
  // A seller answered "Was this article helpful?" (`helpful`: true or false), to find the articles that let them down.
  HELP_ARTICLE_RATED: 'help.article.rated',

  // --- Auth ---
  SIGNUP_COMPLETED: 'auth.signup.completed',
  LOGIN_SUCCEEDED: 'auth.login.succeeded',
  LOGIN_FAILED: 'auth.login.failed',
  LOGOUT: 'auth.logout',
  // Firebase hosts the reset and verification pages, so the app sees the request and the
  // resend, never the completion — there is no client moment to fire a `.completed` on.
  PASSWORD_RESET_REQUESTED: 'auth.password_reset.requested',
  EMAIL_VERIFY_RESENT: 'auth.email_verify.resent',

  // --- Platforms / integrations ---
  PLATFORM_CONNECT_STARTED: 'platform.connect.started',
  PLATFORM_CONNECT_SUCCEEDED: 'platform.connect.succeeded',
  PLATFORM_CONNECT_FAILED: 'platform.connect.failed',
  PLATFORM_CONNECT_CANCELLED: 'platform.connect.cancelled',
  // A marketplace offered as a beta (Grailed) says so before the seller connects it. This is the seller
  // choosing not to go on; going on is the `connect.started` that follows.
  PLATFORM_BETA_NOTICE_DECLINED: 'platform.beta_notice.declined',
  // Poshmark and Depop cannot be connected without the browser extension. This is the seller being
  // sent to add it — a separate event from `connect.started`, which stays the moment the connect
  // itself is attempted, so neither counts the other's sellers.
  EXTENSION_INSTALL_PROMPTED: 'extension.install.prompted',
  // The same wall reached on a phone, where there is no extension to add. Kept apart from
  // `install.prompted` because these sellers were never offered an install — counting them together
  // would read as intent to install from people who were shown no such thing.
  EXTENSION_NEEDS_COMPUTER_SHOWN: 'extension.needs_computer.shown',
  // The app asked the extension whether it was there and got no usable answer. Until this existed
  // nothing recorded that at all: a seller whose extension stopped responding looked identical to
  // one who never installed it, and the only way we ever found a case was by noticing a support
  // ticket. Fired when the answer TURNS to unavailable (not on every re-check, which happens on
  // every tab focus), and always when it blocks a publish — that one is the costly case.
  // `onMobile` separates the sellers who simply cannot have an extension from the ones whose
  // extension broke; without it the phone majority would swamp the signal.
  EXTENSION_UNAVAILABLE: 'extension.unavailable',
  PLATFORM_DISCONNECT_CLICKED: 'platform.disconnect.clicked',
  PLATFORM_DISCONNECT_SUCCEEDED: 'platform.disconnect.succeeded',
  PLATFORM_AUTO_DELIST_TOGGLED: 'platform.auto_delist.toggled',

  // --- Listings: create / import / edit ---
  LISTING_CREATE_STARTED: 'listing.create.started',
  LISTING_CREATE_SUCCEEDED: 'listing.create.succeeded',
  LISTING_EDIT_OPENED: 'listing.edit.opened',
  LISTING_EDIT_SAVED: 'listing.edit.saved',
  LISTING_PUBLISH_SUBMITTED: 'listing.publish.submitted',
  LISTING_IMPORT_STARTED: 'listing.import.started',
  // An import the seller started that never produced listings. Without it a marketplace imported
  // through the browser can fail for every seller who tries and leave nothing behind to see it by.
  LISTING_IMPORT_FAILED: 'listing.import.failed',
  LISTING_IMPORT_COMPLETED: 'listing.import.completed',
  LISTING_IMPORT_SELECTION_OPENED: 'listing.import.selection_opened',
  LISTING_IMPORT_SELECTION_CANCELLED: 'listing.import.selection_cancelled',
  LISTING_CSV_IMPORT_STARTED: 'listing.csv_import.started',
  LISTING_CSV_IMPORT_COMPLETED: 'listing.csv_import.completed',
  LISTING_CSV_IMPORT_FAILED: 'listing.csv_import.failed',
  LISTING_INKFROG_IMPORT_STARTED: 'listing.inkfrog_import.started',
  LISTING_INKFROG_IMPORT_COMPLETED: 'listing.inkfrog_import.completed',

  // --- Listings: AI generation (credit-metered) ---
  LISTING_AI_GENERATE_STARTED: 'listing.ai_generate.started',
  LISTING_AI_GENERATE_SUCCEEDED: 'listing.ai_generate.succeeded',
  LISTING_AI_GENERATE_FAILED: 'listing.ai_generate.failed',

  // --- Listings: images ---
  LISTING_IMAGE_UPLOADED: 'listing.image.uploaded',
  LISTING_IMAGE_EDITOR_OPENED: 'listing.image.editor_opened',
  LISTING_IMAGE_EDITOR_SAVED: 'listing.image.editor_saved',
  LISTING_IMAGE_REORDERED: 'listing.image.reordered',

  // --- Listings: scheduled publish ---
  LISTING_SCHEDULE_SUBMITTED: 'listing.schedule.submitted',
  LISTING_SCHEDULE_CANCELLED: 'listing.schedule.cancelled',
  LISTING_SCHEDULE_RESCHEDULED: 'listing.schedule.rescheduled',

  // --- Listings: duplicate detection ---
  LISTING_DUPLICATE_CHECK_SHOWN: 'listing.duplicate_check.shown',
  LISTING_DUPLICATE_CHECK_RESOLVED: 'listing.duplicate_check.resolved',
  LISTING_DUPLICATE_CHECK_DISMISSED: 'listing.duplicate_check.dismissed',
  // The decisions the seller made are recorded by `.resolved`; this is what the merges
  // actually did, since an individual merge can fail without failing the batch.
  LISTING_DUPLICATE_MERGE_COMPLETED: 'listing.duplicate_merge.completed',

  // --- Listings: lifecycle actions ---
  LISTING_DELIST_STARTED: 'listing.delist.started',
  LISTING_DELIST_SUBMITTED: 'listing.delist.submitted',
  LISTING_DELIST_COMPLETED: 'listing.bulk_delist.completed',
  LISTING_DELIST_RELIST_STARTED: 'listing.delist_relist.started',
  LISTING_DELIST_RELIST_SUBMITTED: 'listing.delist_relist.submitted',
  LISTING_DELIST_RELIST_COMPLETED: 'listing.delist_relist.completed',
  LISTING_ARCHIVE_STARTED: 'listing.archive.started',
  LISTING_ARCHIVE_COMPLETED: 'listing.archive.completed',
  LISTING_MARK_SOLD: 'listing.mark_sold.submitted',
  LISTING_MARK_SOLD_DIALOG_OPENED: 'listing.mark_sold.dialog_opened',
  LISTING_UNMANAGE: 'listing.unmanage.submitted',
  LISTING_RESTORE_COMPLETED: 'listing.restore.completed',
  LISTING_DELETE_COMPLETED: 'listing.delete.completed',
  LISTING_PLATFORM_ACCOUNT_DEACTIVATED: 'listing.platform_account.deactivated',
  LISTING_PULL_FROM_PLATFORM_COMPLETED: 'listing.pull_from_platform.completed',
  LISTING_USE_AS_TEMPLATE_CLICKED: 'listing.use_as_template.clicked',

  // --- Bulk actions ---
  BULK_ACTION_SELECTED: 'bulk.action.selected',
  BULK_EDIT_STARTED: 'bulk.edit.started',
  BULK_EDIT_SUBMITTED: 'bulk.edit.submitted',
  BULK_EDIT_COMPLETED: 'bulk.edit.completed',

  // --- Cross-listing wizard ---
  CROSSLIST_WIZARD_OPENED: 'crosslist.wizard.opened',
  CROSSLIST_WIZARD_STEP_COMPLETED: 'crosslist.wizard.step_completed',
  CROSSLIST_ANALYZE_STARTED: 'crosslist.analyze.started',
  CROSSLIST_ANALYZE_COMPLETED: 'crosslist.analyze.completed',
  CROSSLIST_ANALYZE_FAILED: 'crosslist.analyze.failed',
  CROSSLIST_EXECUTE_SUBMITTED: 'crosslist.execute.submitted',
  CROSSLIST_BATCH_COMPLETED: 'crosslist.batch.completed',
  // One post on one marketplace. These two are a matched pair: every attempt to create a post from
  // the seller's browser emits exactly one of them, so together they count every attempt and a
  // publish success rate can be read off them. A success carrying `alreadyCreated: true` posted
  // nothing — another tab had already claimed the work — and belongs on neither side of that rate.
  CROSSLIST_PLATFORM_SUCCEEDED: 'crosslist.platform.succeeded',
  CROSSLIST_PLATFORM_FAILED: 'crosslist.platform.failed',
  CROSSLIST_WIZARD_STEP_BACK: 'crosslist.wizard.step_back',
  CROSSLIST_RUN_CANCELLED: 'crosslist.run.cancelled',
  // A marketplace refused this run as automation, so the run stopped sending it anything more.
  // Counted because we cannot otherwise see a seller hitting this: the refusal reaches us only as
  // individual publish failures, which look the same whether one item was refused or a whole closet.
  CROSSLIST_RUN_BLOCKED_AS_AUTOMATION: 'crosslist.run.blocked_as_automation',
  // A Poshmark subcategory was dropped on save because it does not belong to the category being
  // sent. Counted because the drop is silent by design: without this, the only way to learn the
  // field is being filled wrongly is a seller hitting the rejection it exists to prevent.
  LISTING_POSHMARK_FEATURES_DROPPED: 'listing.poshmark.category_features_dropped',
  // Depop's categories, or one item's Depop sizes, could not be loaded — even after asking the
  // seller's browser a second time — so the seller was told to try again and the Depop save waits.
  // Counted because nothing else records a seller stopped here: the extension logs its own fetch
  // errors, but not that a seller was left without categories or sizes.
  DEPOP_CATEGORIES_UNAVAILABLE: 'listing.depop.categories_unavailable',
  DEPOP_SIZES_UNAVAILABLE: 'listing.depop.sizes_unavailable',

  // --- Merge duplicates wizard ---
  MERGE_SETUP_OPENED: 'merge.setup.opened',
  MERGE_DETECT_COMPLETED: 'merge.detect.completed',
  MERGE_GROUP_EDITED: 'merge.group.edited',
  MERGE_EXECUTE_SUBMITTED: 'merge.execute.submitted',
  MERGE_BATCH_COMPLETED: 'merge.batch.completed',
  MERGE_GROUP_FAILED: 'merge.group.failed',

  // --- Description templates (Studio) ---
  TEMPLATE_CREATE_COMPLETED: 'template.create.completed',
  TEMPLATE_UPDATE_COMPLETED: 'template.update.completed',
  TEMPLATE_DELETE_COMPLETED: 'template.delete.completed',
  TEMPLATE_DUPLICATE_COMPLETED: 'template.duplicate.completed',
  TEMPLATE_APPLY_SUBMITTED: 'template.apply.submitted',

  // --- Plan limits & upgrade funnel ---
  PLAN_LIMIT_REACHED: 'plan.limit.reached',
  PLAN_UPGRADE_POPOVER_SHOWN: 'plan.upgrade_popover.shown',
  PLAN_UPGRADE_CLICKED: 'plan.upgrade.clicked',

  // --- Marketplace settings (policies, locations, profiles, preferences) ---
  SETTINGS_POLICY_SAVED: 'settings.policy.saved',
  SETTINGS_LOCATION_SAVED: 'settings.location.saved',
  SETTINGS_SHIPPING_PROFILE_SAVED: 'settings.shipping_profile.saved',
  SETTINGS_STORE_PREFERENCES_SAVED: 'settings.store_preferences.saved',
  // Timezone is a field on the account form, so it rides along as a property here rather
  // than as its own event. eBay Store categories are read-only from eBay — nothing to save.
  SETTINGS_ACCOUNT_SAVED: 'settings.account.saved',

  // --- Billing ---
  BILLING_PRICING_VIEWED: 'billing.pricing.viewed',
  BILLING_PLAN_SELECTED: 'billing.plan.selected',
  BILLING_CHECKOUT_STARTED: 'billing.checkout.started',
  BILLING_CHECKOUT_FAILED: 'billing.checkout.failed',
  BILLING_ADDON_CHECKOUT_STARTED: 'billing.addon.checkout_started',
  BILLING_PORTAL_OPENED: 'billing.portal.opened',
  BILLING_CANCEL_OPENED: 'billing.cancel.opened',
  BILLING_CANCEL_SUBMITTED: 'billing.cancel.submitted',
  BILLING_CANCEL_KEPT_PLAN: 'billing.cancel.kept_plan',
  BILLING_SUBSCRIBED: 'billing.subscribed',

  // --- Onboarding ---
  ONBOARDING_MODAL_SHOWN: 'onboarding.modal.shown',
  ONBOARDING_MODAL_DISMISSED: 'onboarding.modal.dismissed',
  ONBOARDING_MODAL_CTA_CLICKED: 'onboarding.modal.cta_clicked',
  ONBOARDING_TIP_SHOWN: 'onboarding.tip.shown',
  ONBOARDING_TIP_CLICKED: 'onboarding.tip.clicked',
  // Guided setup. `step` carries the step name, so one funnel covers the whole flow.
  ONBOARDING_SETUP_STARTED: 'onboarding.setup.started',
  ONBOARDING_SETUP_STEP_VIEWED: 'onboarding.setup.step_viewed',
  ONBOARDING_SETUP_GOAL_CHOSEN: 'onboarding.setup.goal_chosen',
  ONBOARDING_SETUP_SKIPPED: 'onboarding.setup.skipped',
  ONBOARDING_SETUP_COMPLETED: 'onboarding.setup.completed',

  // --- Cross-cutting ---
  CTA_BLOCKED: 'cta.blocked',
  API_REQUEST_FAILED: 'api.request.failed',
});
